import { isDebugEnv } from '~/lib/env'
import { forceCountdownDays, timestamp } from '~/lib/params'

const DAY_MS = 24 * 60 * 60 * 1000

const eventStartDateByEnv: Record<string, Date> = {
  development: new Date(timestamp.getTime() - 4 * DAY_MS),
  staging: new Date('2026-05-18T00:00:00+00:00'),
  production: new Date('2026-05-27T00:00:00+00:00'),
}

// In local dev, anchor the window to the URL's `ts` param (same source
// `useTimestamp()` seeds from) and shift 4 days into the past so today lands
// on day 5 — matching the default `seedProgresses` fixture which plants
// missed/opened/finish/finish on days 1–4 and an active baking pair on day 5.
//
// `?force_countdown=N` (dev/staging only) overrides the start date to
// `timestamp + N days` so the pre-launch countdown can be exercised
// regardless of the env's actual start.
// Fall back to development for unrecognized MODEs (vitest's 'test', and the
// `dev` deploy mode which isn't in the table above).
const baseEventStartDate =
  eventStartDateByEnv[import.meta.env.MODE] ?? eventStartDateByEnv.development

export const eventStartDate =
  forceCountdownDays !== null && isDebugEnv
    ? new Date(timestamp.getTime() + forceCountdownDays * DAY_MS)
    : baseEventStartDate

export const eventEndDate = import.meta.env.DEV
  ? new Date(baseEventStartDate.getTime() + 30 * DAY_MS - 1)
  : import.meta.env.MODE === 'production'
    ? new Date(new Date('2026-06-26T00:00:00+00:00').getTime() - 1)
    : new Date(new Date('2026-06-17T00:00:00+00:00').getTime() - 1)

export { getActivityId } from './lib/activity-id'

const frostingTokenIdByEnv = {
  production: 1,
  staging: 1,
  dev: 1,
  development: 1,
} as Record<string, number>

export const frostingTokenId = frostingTokenIdByEnv[import.meta.env.MODE]

const coinBoxProductIdByEnv = {
  production: 83,
  staging: 83,
  dev: 83,
  development: 83,
} as Record<string, number>
const gemBoxProductIdByEnv = {
  production: 84,
  staging: 84,
  dev: 84,
  development: 84,
} as Record<string, number>

export const coinBoxProductId = coinBoxProductIdByEnv[import.meta.env.MODE]
export const gemBoxProductId = gemBoxProductIdByEnv[import.meta.env.MODE]

export type BoostingPackTier = 'small' | 'mega'

export interface BoostingPack {
  /** Service-API product id used to create the order. */
  productId: number
  /** Store SKU used by the native purchase and the receipt system. */
  sku: string
  tier: BoostingPackTier
  /** Reward amounts shown on the card (display only — granted server-side). */
  rewards: { cookies: number; coins: number; gems: number }
  /** Bonus badge percentage shown on the card, if any. */
  bonusPercent?: number
  /** Label shown until the store-localized price loads (or in dev). */
  fallbackPrice: string
}

// One-time consumable IAP "Boosting Packs". Product ids are stable across
// clusters for this campaign, so they're not split per-env like the shop.
export const boostingPacks: BoostingPack[] = [
  {
    productId: 111,
    sku: 'package_2026_birthday_1',
    tier: 'small',
    rewards: { cookies: 100, coins: 1000, gems: 100 },
    fallbackPrice: '2.99 USD',
  },
  {
    productId: 112,
    sku: 'package_2026_birthday_2',
    tier: 'mega',
    rewards: { cookies: 450, coins: 3500, gems: 300 },
    bonusPercent: 50,
    fallbackPrice: '9.99 USD',
  },
]

export type ShopItemConfig = {
  productId: number
  treeTypeId: number
  isPlusOnly: boolean
  /** Asset path imported via Vite (a 1:1 src/assets/... reference). */
  asset: string
}

// Tree-type ids are stable across clusters; product ids are not, so we keep
// the static catalog (treeTypeId + asset + Plus flag) separate from the
// per-env productId lookup.
//
// Catalog shape:
// - 11 bakery items (treeTypeIds 164–174). Tree 169 is the 12th Anniversary
//   special priced at 150 stamps; the other 10 are 80 stamps.
// - 15 Plus-limited past-event trees (113, 115, 117, 118, 124, 125, 126, 130,
//   131, 135, 138, 140, 143, 147, 148) at 80 stamps.
//
// Card art is the same `tree{id}-*.webp` used by the reward reveal, so the
// shop preview and the reward page always agree on which tree the user is
// buying.
const treeAssetUrls = import.meta.glob('./assets/reward-trees/tree*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

const TREE_ASSETS = new Map<number, string>()
for (const [path, url] of Object.entries(treeAssetUrls)) {
  if (path.includes('_blank-')) continue
  const match = path.match(/tree(\d+)-/)
  if (match) TREE_ASSETS.set(Number(match[1]), url)
}

function treeAsset(treeTypeId: number): string {
  const url = TREE_ASSETS.get(treeTypeId)
  if (!url) {
    throw new Error(`Missing reward-tree asset for treeTypeId ${treeTypeId}`)
  }
  return url
}

const BAKERY_TREE_IDS = [164, 165, 166, 167, 168, 169, 170, 171, 172, 173, 174]
const PLUS_TREE_IDS = [
  113, 115, 117, 118, 124, 125, 126, 130, 131, 135, 138, 140, 143, 147, 148,
]

const STATIC_CATALOG: Omit<ShopItemConfig, 'productId'>[] = [
  ...BAKERY_TREE_IDS.map((treeTypeId) => ({
    treeTypeId,
    isPlusOnly: false,
    asset: treeAsset(treeTypeId),
  })),
  ...PLUS_TREE_IDS.map((treeTypeId) => ({
    treeTypeId,
    isPlusOnly: true,
    asset: treeAsset(treeTypeId),
  })),
]

// Production cluster product ids (confirmed 2026-05-25).
const PRODUCTION_PRODUCT_IDS: Record<number, number> = {
  164: 85,
  165: 86,
  166: 87,
  167: 88,
  168: 89,
  169: 90,
  170: 91,
  171: 92,
  172: 93,
  173: 94,
  174: 95,
  113: 96,
  115: 97,
  117: 98,
  118: 99,
  124: 100,
  125: 101,
  126: 102,
  130: 103,
  131: 104,
  135: 105,
  138: 106,
  140: 107,
  143: 108,
  147: 109,
  148: 110,
}

// Staging cluster product ids (confirmed 2026-05-17). Reused in local dev
// and tests so MSW round-trips against the same ids backend will serve.
const STAGING_PRODUCT_IDS: Record<number, number> = {
  164: 100,
  165: 101,
  166: 102,
  167: 103,
  168: 104,
  169: 105,
  170: 106,
  171: 107,
  172: 108,
  173: 109,
  174: 110,
  113: 85,
  115: 86,
  117: 87,
  118: 88,
  124: 89,
  125: 90,
  126: 91,
  130: 92,
  131: 93,
  135: 94,
  138: 95,
  140: 96,
  143: 97,
  147: 98,
  148: 99,
}

const productIdsByEnv: Record<string, Record<number, number>> = {
  production: PRODUCTION_PRODUCT_IDS,
  staging: STAGING_PRODUCT_IDS,
  dev: STAGING_PRODUCT_IDS,
  development: STAGING_PRODUCT_IDS,
}

const productIds = productIdsByEnv[import.meta.env.MODE] ?? STAGING_PRODUCT_IDS

export const shopCatalog: ShopItemConfig[] = STATIC_CATALOG.map((item) => ({
  ...item,
  productId: productIds[item.treeTypeId] ?? 0,
}))

/** Product id for the 12th Anniversary Cake Tree (treeTypeId 169), the only
 *  bakery-tab item priced at 150 stamps instead of 80. */
export const anniversaryCakeProductId: number = productIds[169] ?? 0

export type CakeKind = 'macaron' | 'cupcake' | 'special'

/** The 11 limited-edition cakes on the collection cabinet. */
export const cakeCollection: { treeTypeId: number; kind: CakeKind }[] = [
  { treeTypeId: 164, kind: 'macaron' },
  { treeTypeId: 165, kind: 'macaron' },
  { treeTypeId: 166, kind: 'macaron' },
  { treeTypeId: 167, kind: 'macaron' },
  { treeTypeId: 168, kind: 'macaron' },
  { treeTypeId: 170, kind: 'cupcake' },
  { treeTypeId: 171, kind: 'cupcake' },
  { treeTypeId: 172, kind: 'cupcake' },
  { treeTypeId: 173, kind: 'cupcake' },
  { treeTypeId: 174, kind: 'cupcake' },
  { treeTypeId: 169, kind: 'special' },
]
